import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Gauge, Users, Target, Gift, Trophy, Megaphone, Layers, Crown, Ticket, Server,
  ExternalLink, LogOut, Menu, X, Droplet, Radio,
} from "lucide-react";

export const ADMIN_NAV = [
  {
    group: "Overview",
    items: [
      { key: "overview", label: "Dashboard", icon: Gauge },
      { key: "players", label: "Players", icon: Users },
    ],
  },
  {
    group: "Quests",
    items: [{ key: "quests", label: "Quest portal", icon: Target, badge: "quests" }],
  },
  {
    group: "Catalogue",
    items: [
      { key: "rewards", label: "Rewards & queue", icon: Gift, badge: "redemptions" },
      { key: "achievements", label: "Achievements", icon: Trophy },
      { key: "offers", label: "Offers", icon: Megaphone },
      { key: "levels", label: "Levels", icon: Layers },
    ],
  },
  {
    group: "Engagement",
    items: [
      { key: "podium", label: "Weekly podium", icon: Crown },
      { key: "giveaway", label: "Giveaway", icon: Ticket, badge: "giveaway" },
      { key: "watch", label: "Watch & earn", icon: Radio },
    ],
  },
  {
    group: "Server",
    items: [{ key: "connect", label: "Connect & resource", icon: Server }],
  },
];

const ALL_KEYS = ADMIN_NAV.flatMap((g) => g.items.map((i) => i.key));

export const useAdminSection = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const active = ALL_KEYS.includes(section) ? section : "overview";
  const setActive = (key) => navigate(`/admin/${key}`);
  return [active, setActive];
};

export const AdminShell = ({ active, onNavigate, counts, title, subtitle, children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const NavItems = ({ suffix = "" }) => (
    <nav className="space-y-7">
      {ADMIN_NAV.map((g) => (
        <div key={g.group}>
          <div className="px-3 text-[9px] uppercase tracking-[0.24em] text-muted-foreground/70">
            {g.group}
          </div>
          <div className="mt-2 space-y-0.5">
            {g.items.map((item) => {
              const Icon = item.icon;
              const count = item.badge ? counts?.[item.badge] || 0 : 0;
              const isActive = active === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    onNavigate(item.key);
                    setOpen(false);
                  }}
                  data-testid={`admin-nav-${item.key}${suffix}`}
                  className={`flex w-full items-center gap-3 border-l-2 px-3 py-2 text-left text-xs uppercase tracking-[0.14em] transition-colors ${
                    isActive
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {count > 0 && (
                    <span className="mono border border-amber-400/50 bg-amber-400/10 px-1.5 text-[10px] text-amber-300"
                      data-testid={`admin-badge-${item.key}${suffix}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-shell">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-card/60 py-6 lg:flex">
        <div className="flex items-center gap-2 px-5">
          <Droplet className="h-5 w-5 text-primary" />
          <div>
            <div className="text-sm font-semibold leading-none">LiquidRP</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.24em] text-primary">Control room</div>
          </div>
        </div>
        <div className="mt-8 flex-1 overflow-y-auto px-2">
          <NavItems />
        </div>
        <div className="px-5 pt-4 text-[10px] text-muted-foreground">
          <div className="truncate" data-testid="admin-identity">{user?.email || user?.username}</div>
          <div className="mt-0.5 uppercase tracking-[0.2em] text-primary/70">Staff</div>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="absolute inset-0 bg-background/80" onClick={() => setOpen(false)} />
          <aside className="relative z-50 h-full w-64 overflow-y-auto border-r border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.24em] text-primary">Control room</span>
              <button type="button" onClick={() => setOpen(false)} data-testid="admin-nav-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6">
              <NavItems suffix="-mobile" />
            </div>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-background/95 px-5 py-3 backdrop-blur">
          <button type="button" className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-nav-open">
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{title}</div>
            {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
          </div>
          {counts?.total > 0 && (
            <button
              type="button"
              onClick={() => onNavigate("overview")}
              className="mono hidden border border-amber-400/50 bg-amber-400/10 px-3 py-1.5 text-[11px] text-amber-300 sm:block"
              data-testid="admin-pending-total"
            >
              {counts.total} awaiting action
            </button>
          )}
          <Button variant="outline" size="sm" className="rounded-none" asChild>
            <a href="/dashboard" target="_blank" rel="noreferrer" data-testid="admin-view-player-site">
              <ExternalLink className="mr-2 h-3.5 w-3.5" /> Player site
            </a>
          </Button>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="admin-logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>

        <main className="min-w-0 flex-1 px-5 py-8 lg:px-8" data-testid="admin-page">
          {children}
        </main>
      </div>
    </div>
  );
};

export const useAdminCounts = () => {
  const [counts, setCounts] = useState({ redemptions: 0, quests: 0, giveaway: 0, total: 0 });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pending-counts");
      setCounts(data);
    } catch {
      /* keep the last known counts */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  return [counts, refresh];
};

export const NeedsAction = ({ counts, onNavigate }) => {
  const lanes = [
    { key: "redemptions", label: "Reward redemptions", hint: "Deliver or reject pending rewards", target: "rewards", icon: Gift },
    { key: "quests", label: "Quest submissions", hint: "Approve proof and pay out points", target: "quests", icon: Target },
    { key: "giveaway", label: "Giveaway claims", hint: "Winners waiting to be paid out", target: "giveaway", icon: Ticket },
  ];
  return (
    <div className="border border-border bg-card" data-testid="needs-action">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <h3 className="text-base font-semibold md:text-lg">Needs action</h3>
        <span className="mono text-xs text-muted-foreground">{counts.total || 0} open</span>
      </div>
      <div className="grid sm:grid-cols-3">
        {lanes.map((lane) => {
          const Icon = lane.icon;
          const n = counts[lane.key] || 0;
          return (
            <button
              key={lane.key}
              type="button"
              onClick={() => onNavigate(lane.target)}
              className="group border-b border-border/60 px-5 py-5 text-left transition-colors last:border-0 hover:bg-primary/5 sm:border-b-0 sm:border-r sm:last:border-r-0"
              data-testid={`needs-action-${lane.key}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-4 w-4 ${n ? "text-amber-300" : "text-muted-foreground"}`} />
                <span className={`mono text-2xl font-semibold ${n ? "text-amber-300" : "text-muted-foreground"}`}>
                  {n}
                </span>
              </div>
              <div className="mt-3 text-sm font-medium">{lane.label}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {n ? lane.hint : "All clear"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
